interface KpiCardProps {
  label: string;
  value: string;
  icon: string;
  tone: 'info' | 'primary' | 'success' | 'accent';
  note?: string;
  highlight?: boolean;
  trend?: { pct: string; up: boolean };
  delay?: number;
}

const toneStyles: Record<KpiCardProps['tone'], {
  iconBg: string;
  iconText: string;
  accent: string;
  glow: string;
  badge: string;
  badgeText: string;
  barBg: string;
}> = {
  info: {
    iconBg: 'bg-secondary-100',
    iconText: 'text-secondary-600',
    accent: 'bg-secondary-500',
    glow: 'shadow-[0_0_0_1px_oklch(var(--secondary-200))]',
    badge: 'bg-secondary-50 text-secondary-700',
    badgeText: 'text-secondary-500',
    barBg: 'bg-secondary-400',
  },
  primary: {
    iconBg: 'bg-primary-100',
    iconText: 'text-primary-600',
    accent: 'bg-primary-500',
    glow: 'shadow-[0_0_0_1px_oklch(var(--primary-200))]',
    badge: 'bg-primary-50 text-primary-700',
    badgeText: 'text-primary-500',
    barBg: 'bg-primary-400',
  },
  success: {
    iconBg: 'bg-success-100',
    iconText: 'text-success-600',
    accent: 'bg-success-500',
    glow: '',
    badge: 'bg-success-50 text-success-700',
    badgeText: 'text-success-500',
    barBg: 'bg-success-500',
  },
  accent: {
    iconBg: 'bg-accent-100',
    iconText: 'text-accent-700',
    accent: 'bg-accent-500',
    glow: '',
    badge: 'bg-accent-50 text-accent-700',
    badgeText: 'text-accent-600',
    barBg: 'bg-accent-400',
  },
};

export default function KpiCard({ label, value, icon, tone, note, highlight, trend, delay = 0 }: KpiCardProps) {
  const s = toneStyles[tone];

  return (
    <div
      className="animate-fade-up group relative overflow-hidden rounded-card border border-background-200 bg-white p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-secondary-200 hover:shadow-soft"
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Top accent strip */}
      <div className={`absolute inset-x-0 top-0 h-[3px] ${s.accent} opacity-80`} />

      <div className="flex items-start justify-between">
        {/* Icon */}
        <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${s.iconBg} ${s.iconText} transition-transform duration-300 group-hover:scale-110`}>
          <i className={`${icon} text-[22px] leading-none`} aria-hidden="true" />
        </span>

        <div className="flex flex-col items-end gap-1.5">
          {/* Trend badge */}
          {trend && (
            <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${s.badge}`}>
              <i className={trend.up ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} />
              {trend.pct}
            </span>
          )}
          {/* highlight dot */}
          {highlight && (
            <span className="flex items-center gap-1.5 rounded-full bg-warning-50 px-2 py-1 text-[11px] font-medium text-warning-700">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-warning-400 opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-warning-500" />
              </span>
              يحتاج إجراء
            </span>
          )}
        </div>
      </div>

      {/* Value */}
      <p className="mt-4 font-bold text-[30px] leading-none tracking-tight text-foreground-950">
        {value}
      </p>

      {/* Label */}
      <p className="mt-1.5 text-[13px] text-foreground-500">{label}</p>

      {/* Note + bar */}
      {note && (
        <div className="mt-3 space-y-1.5">
          <p className={`text-[11px] font-medium ${s.badgeText}`}>{note}</p>
          <div className="h-1 w-full overflow-hidden rounded-full bg-background-100">
            <div className={`h-full w-2/3 rounded-full ${s.barBg} opacity-60 transition-all duration-700 group-hover:opacity-90`} />
          </div>
        </div>
      )}
    </div>
  );
}